import React from 'react';
import { motion } from 'framer-motion';
import { DropletsIcon, WrenchIcon, HardHatIcon, ShieldIcon, WavesIcon, ZapIcon, ArrowRightIcon } from 'lucide-react';
const SERVICES = [{
  title: 'Gutter Cleaning',
  desc: 'Remove debris, leaves, and buildup to keep water flowing freely and protect your foundation.',
  icon: DropletsIcon,
  accent: '#0B7AC4',
  season: 'Spring & Fall'
}, {
  title: 'Gutter Repair',
  desc: 'Fix leaks, sagging, and damage before they cause costly water damage to your home.',
  icon: WrenchIcon,
  accent: '#F97316',
  season: 'Year-Round'
}, {
  title: 'Gutter Installation',
  desc: 'Custom-fit seamless gutters installed to protect your home for decades to come.',
  icon: HardHatIcon,
  accent: '#FFD400',
  season: 'Spring & Summer'
}, {
  title: 'Gutter Guards',
  desc: 'Keep gutters clog-free year-round with professional, high-quality guard systems.',
  icon: ShieldIcon,
  accent: '#7AC943',
  season: 'Year-Round'
}, {
  title: 'Underground Drain Cleaning',
  desc: 'Clear underground drains to prevent flooding, backups, and foundation water damage.',
  icon: WavesIcon,
  accent: '#7DD3FC',
  season: 'Spring'
}, {
  title: 'Underground Drain Installation',
  desc: 'Professional installation of robust underground drainage systems to route water away.',
  icon: HardHatIcon,
  accent: '#0B7AC4',
  season: 'Spring & Summer'
}, {
  title: 'Power Washing',
  desc: 'Restore your home exterior, siding, and walkways with high-pressure professional cleaning.',
  icon: ZapIcon,
  accent: '#FFD400',
  season: 'Summer'
}];
export function ServicesSection() {
  return <section id="services" className="relative py-24 bg-gray-50 overflow-hidden" data-id="element-318">
      {/* Subtle background texture */}
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
      backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(11,122,196,0.06) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(122,201,67,0.06) 0%, transparent 50%)'
    }} data-id="element-319" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-320">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="text-center mb-16" data-id="element-321">
          <p className="text-[#0B7AC4] font-bold uppercase tracking-widest text-sm mb-3" data-id="element-322">
            What We Do
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-[#0A1628] mb-4" data-id="element-323">
            Our Services
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#0B7AC4] to-[#7AC943] mx-auto rounded-full mb-6" data-id="element-324" />
          <p className="text-xl text-gray-500 max-w-2xl mx-auto font-light" data-id="element-325">
            Comprehensive exterior maintenance solutions to protect and enhance
            your property.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-id="element-326">
          {SERVICES.map((service, index) => <motion.div key={service.title} initial={{
          opacity: 0,
          y: 32
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.08,
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} className="gradient-border-card group bg-white p-8 shadow-md hover:shadow-2xl transition-all duration-400 hover:-translate-y-2 cursor-pointer" data-id="element-327">
              {/* Season tag */}
              <div className="flex justify-between items-start mb-6" data-id="element-328">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3" style={{
              background: `${service.accent}15`,
              border: `1px solid ${service.accent}30`
            }} data-id="element-329">
                  <service.icon className="w-6 h-6 transition-colors duration-300" style={{
                color: service.accent
              }} data-id="element-330" />
                </div>
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider" style={{
              background: `${service.accent}15`,
              color: service.accent
            }} data-id="element-331">
                  {service.season}
                </span>
              </div>

              <h3 className="text-xl font-bold text-[#0A1628] mb-3 group-hover:text-[#0B7AC4] transition-colors duration-300 uppercase tracking-wide" data-id="element-332">
                {service.title}
              </h3>
              <p className="text-gray-500 mb-6 leading-relaxed text-sm" data-id="element-333">
                {service.desc}
              </p>

              <a href="#contact" className="inline-flex items-center font-bold text-sm transition-all duration-300 group-hover:gap-2" style={{
            color: service.accent
          }} data-id="element-334">
                Get Free Estimate
                <ArrowRightIcon className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" data-id="element-335" />
              </a>

              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
            background: service.accent
          }} data-id="element-336" />
            </motion.div>)}
        </div>

        {/* Bottom CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.5
      }} className="mt-16 text-center" data-id="element-337">
          <p className="text-gray-500 mb-6" data-id="element-338">Not sure which service you need?</p>
          <a href="tel:8624072386" className="btn-yellow inline-flex items-center gap-2 text-lg" data-id="element-339">
            Call Us for a Free Consultation
          </a>
        </motion.div>
      </div>
    </section>;
}